<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ChoiceToggle from 'dashboard/components-next/input/ChoiceToggle.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import {
  MAX_CONDITIONS,
  entityFields,
  fieldRef,
  findField,
  newCondition,
  parseSourceKey,
  sourceKey,
  visibleEntities,
} from './widgetConfig';

const props = defineProps({
  unit: { type: String, required: true },
  catalog: { type: Object, default: () => ({}) },
});

const conditions = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();

// Clicar na opção marcada faz o ComboBox limpar a própria seleção: remonta
// os seletores para voltarem a mostrar o valor atual.
const resetKey = ref(0);

const STAGE_TYPES = ['regular', 'won', 'lost'];
const VALUELESS_OPERATORS = ['filled', 'empty'];

const entityLabel = entity =>
  t(`COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.ENTITIES.${entity.toUpperCase()}`);

const sourceOptionsFor = entity => {
  if (entity !== 'deal') {
    return [
      {
        value: `label:${entity}`,
        label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.LABEL', {
          entity: entityLabel(entity),
        }),
      },
      {
        value: `field:${entity}`,
        label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.FIELD', {
          entity: entityLabel(entity),
        }),
      },
    ];
  }
  return [
    {
      value: 'label:deal',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.LABEL', {
        entity: entityLabel('deal'),
      }),
    },
    {
      value: 'pipeline',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.PIPELINE'),
    },
    {
      value: 'stage',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.STAGE'),
    },
    {
      value: 'stage_type',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.STAGE_TYPE'),
    },
    {
      value: 'responsible',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.RESPONSIBLE'),
    },
    {
      value: 'field:deal',
      label: t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.SOURCES.FIELD', {
        entity: entityLabel('deal'),
      }),
    },
  ];
};

// Em Contato/Empresa, as condições de Negociação formam um grupo próprio
// ("tem ao menos uma negociação que…") e valem para a mesma negociação.
const groups = computed(() => {
  const direct = visibleEntities(props.unit).filter(
    entity => props.unit === 'deal' || entity !== 'deal'
  );
  const result = [
    {
      id: 'main',
      entities: direct,
      matches: condition =>
        props.unit === 'deal' || condition.entity !== 'deal',
    },
  ];
  if (props.unit !== 'deal') {
    result.push({
      id: 'deals',
      entities: ['deal'],
      matches: condition => condition.entity === 'deal',
    });
  }
  return result.map(group => ({
    ...group,
    sourceOptions: group.entities.flatMap(sourceOptionsFor),
  }));
});

const canAdd = computed(() => conditions.value.length < MAX_CONDITIONS);

const rowsOf = group => conditions.value.filter(group.matches);

// Estas fontes têm um único valor por registro: duas condições combinadas com
// "E" nunca fariam sentido. Mais de um valor (ex.: dois pipelines) se escolhe
// dentro da mesma condição, que combina os valores com "OU".
const SINGLE_USE_SOURCES = ['pipeline', 'stage', 'stage_type', 'responsible'];

const optionsFor = (group, condition) =>
  group.sourceOptions.filter(
    option =>
      !SINGLE_USE_SOURCES.includes(option.value) ||
      !conditions.value.some(
        other => other !== condition && sourceKey(other) === option.value
      )
  );

const addCondition = group => {
  const { source, entity } = parseSourceKey(optionsFor(group)[0].value);
  conditions.value = [...conditions.value, newCondition(source, entity)];
};

const removeCondition = condition => {
  conditions.value = conditions.value.filter(item => item !== condition);
};

const changeSource = (condition, key) => {
  if (!key) resetKey.value += 1;
  if (!key || key === sourceKey(condition)) return;
  const { source, entity } = parseSourceKey(key);
  Object.assign(condition, newCondition(source, entity), {
    uid: condition.uid,
  });
};

const changeField = (condition, fieldKey) => {
  const field = findField(props.catalog, condition.entity, fieldKey);
  condition.field = fieldKey;
  condition.operator = field?.operators[0] || '';
  condition.values = field?.type === 'checkbox' ? [true] : [];
};

const changeOperator = (condition, operator) => {
  if (!operator) resetKey.value += 1;
  if (!operator) return;
  condition.operator = operator;
  if (VALUELESS_OPERATORS.includes(operator)) condition.values = [];
};

const setSingleValue = (condition, value) => {
  condition.values = value === '' ? [] : [value];
};

const setNumberValue = (condition, value) => {
  condition.values = value === '' ? [] : [Number(value)];
};

const fieldOptions = condition =>
  entityFields(props.catalog, condition.entity)
    .filter(field => field.operators.length)
    .map(field => ({ value: fieldRef(field), label: field.name }));

const operatorOptions = condition => {
  if (condition.source === 'label') {
    return ['any', 'none'].map(operator => ({
      value: operator,
      label: t(
        `COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.OPERATORS.${operator.toUpperCase()}`
      ),
    }));
  }
  if (condition.source !== 'field') return [];
  const field = findField(props.catalog, condition.entity, condition.field);
  return (field?.operators || []).map(operator => ({
    value: operator,
    label: t(
      `COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.OPERATORS.${operator.toUpperCase()}`
    ),
  }));
};

const toIdOptions = (list, label) =>
  (list || []).map(item => ({ value: item.id, label: label(item) }));

const valueOptions = condition => {
  const { catalog } = props;
  switch (condition.source) {
    case 'label':
      return toIdOptions(
        catalog.labels?.[condition.entity],
        item => item.title
      );
    case 'pipeline':
      return toIdOptions(catalog.pipelines, item => item.name);
    case 'stage':
      return (catalog.pipelines || []).flatMap(pipeline =>
        toIdOptions(
          pipeline.stages,
          stage => `${pipeline.name} › ${stage.name}`
        )
      );
    case 'responsible':
      return toIdOptions(catalog.responsibles, item => item.name);
    case 'stage_type':
      return STAGE_TYPES.map(type => ({
        value: type,
        label: t(
          `COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.STAGE_TYPES.${type.toUpperCase()}`
        ),
      }));
    default: {
      const field = findField(catalog, condition.entity, condition.field);
      return (field?.options || []).map(option => ({
        value: option,
        label: option,
      }));
    }
  }
};

// Tipo de entrada do valor: lista de opções, sim/não, número ou nenhum.
const valueKind = condition => {
  if (condition.source === 'stage_type') return 'single';
  if (condition.source !== 'field') return 'multi';
  const field = findField(props.catalog, condition.entity, condition.field);
  if (!field || VALUELESS_OPERATORS.includes(condition.operator)) return 'none';
  if (field.type === 'checkbox') return 'toggle';
  if (['list', 'multiple_choice'].includes(field.type)) return 'multi';
  return 'number';
};

const valuePlaceholder = condition =>
  t(
    `COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.VALUES.${condition.source === 'field' ? 'OPTIONS' : condition.source.toUpperCase()}`
  );
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-for="group in groups" :key="group.id" class="flex flex-col gap-3">
      <p
        v-if="group.id === 'deals'"
        class="mb-0 text-sm font-medium text-n-slate-12"
      >
        {{ t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.DEAL_GROUP_TITLE') }}
      </p>
      <p v-else class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.TITLE') }}
      </p>
      <p
        v-if="group.id === 'deals' && rowsOf(group).length"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.DEAL_GROUP_HINT') }}
      </p>
      <ul
        v-if="rowsOf(group).length"
        class="flex flex-col gap-3 p-0 m-0 list-none"
      >
        <li
          v-for="(condition, index) in rowsOf(group)"
          :key="condition.uid"
          class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <div class="flex items-center gap-2">
            <span
              v-if="index > 0"
              class="flex-shrink-0 text-xs font-medium uppercase text-n-slate-11"
            >
              {{ t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.AND') }}
            </span>
            <ComboBox
              :key="resetKey"
              :model-value="sourceKey(condition)"
              :options="optionsFor(group, condition)"
              @update:model-value="changeSource(condition, $event)"
            />
            <Button
              type="button"
              ghost
              slate
              xs
              icon="i-lucide-trash-2"
              class="flex-shrink-0"
              :aria-label="t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.REMOVE')"
              @click="removeCondition(condition)"
            />
          </div>

          <ComboBox
            v-if="condition.source === 'field'"
            :model-value="condition.field"
            :options="fieldOptions(condition)"
            :placeholder="
              t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.FIELD_PLACEHOLDER')
            "
            :empty-state="t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.NO_FIELDS')"
            @update:model-value="changeField(condition, $event)"
          />

          <template v-if="condition.source !== 'field' || condition.field">
            <ComboBox
              v-if="operatorOptions(condition).length > 1"
              :key="resetKey"
              :model-value="condition.operator"
              :options="operatorOptions(condition)"
              @update:model-value="changeOperator(condition, $event)"
            />
            <span v-else class="text-sm text-n-slate-11">
              {{
                t(
                  `COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.OPERATORS.${condition.operator.toUpperCase()}`
                )
              }}
            </span>

            <TagMultiSelectComboBox
              v-if="valueKind(condition) === 'multi'"
              v-model="condition.values"
              :options="valueOptions(condition)"
              :placeholder="valuePlaceholder(condition)"
              :empty-state="
                t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.NO_OPTIONS')
              "
            />
            <ComboBox
              v-else-if="valueKind(condition) === 'single'"
              :model-value="condition.values[0] ?? ''"
              :options="valueOptions(condition)"
              :placeholder="valuePlaceholder(condition)"
              @update:model-value="setSingleValue(condition, $event)"
            />
            <ChoiceToggle
              v-else-if="valueKind(condition) === 'toggle'"
              :model-value="condition.values[0]"
              @update:model-value="condition.values = [$event]"
            />
            <Input
              v-else-if="valueKind(condition) === 'number'"
              type="number"
              :model-value="condition.values[0] ?? ''"
              :placeholder="
                t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.VALUES.NUMBER')
              "
              @update:model-value="setNumberValue(condition, $event)"
            />
          </template>
        </li>
      </ul>
      <Button
        type="button"
        ghost
        blue
        sm
        icon="i-lucide-plus"
        class="w-fit"
        :disabled="!canAdd"
        :label="
          t(
            group.id === 'deals'
              ? 'COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.ADD_DEAL'
              : 'COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.ADD'
          )
        "
        @click="addCondition(group)"
      />
    </div>
    <p v-if="conditions.length" class="mb-0 text-xs text-n-slate-11">
      {{ t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.OR_HINT') }}
    </p>
    <p v-if="!canAdd" class="mb-0 text-xs text-n-slate-11">
      {{
        t('COMMERCIAL_WIDGETS_DIALOG.CONDITIONS.LIMIT', {
          max: MAX_CONDITIONS,
        })
      }}
    </p>
  </div>
</template>
